import { api } from "@/comn";
import { ptlEnvs } from "@/tra/tancis/ptli/comn";
import { TFormSchema, TGridSchema, useFetch } from "@/comn/hooks";
import { comnEnvs, comnUtils } from "@/comn/utils";

/*
 * Survey Item Management Service
 * !== 설문 항목 관리 서비스 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */

export const BASE = {
    path: `${ptlEnvs.base}/surv/itm`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/surv/itm`,
    nodes: [],
};
/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    ptli0420001q: `${BASE.path}/ptli0420001q`,
    ptli0420002s: `${BASE.path}/ptli0420002s`,
    ptli0420003s: `${BASE.path}/ptli0420003s`,
    ptli0420004q: `${BASE.path}/ptli0420004q`,
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    getSurvItmList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    createSurvItm: (data: any) => {
        return api.post(`${BASE.api}`, {
            ...data,
        });
    },
    getSurvItmInfo: (quesItmId: any) => {
        return api.get(`${BASE.api}/${quesItmId}`);
    },
    updateSurvItmInfo: (data: any) => {
        return api.put(`${BASE.api}/${data.quesItmId}`, { ...data });
    },

    deleteSurvItmInfo: (quesItmId: any) => {
        return api.delete(`${BASE.api}/${quesItmId}`);
    },

    getNextItmOrd: (quesId: string) => {
        return api.get(`${BASE.api}/itmOrd`, {
            params: { quesId },
        });
    },
};

export const SF_SURV_ITM_LIST_SRCH: TFormSchema = {
    id: "form_SurvItmListSrch",
    schema: {
        bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_CD", all: true, area: "comnCd", comnCd: "COM0024" },
        ptlTpCd: {
            required: true,
            type: "select",
            label: "L_PTL_TP_CD",
            area: "comnCd",
            comnCd: "COM0052",
            excludes: ["B"],
            editType: "both",
        },
        qustnrTtle: { type: "text", label: "L_QUSTNR_TTLE" },
        quesCn: { type: "text", label: "L_QUES_CN" },
        survNm: { type: "text", label: "L_ORG_NM" },
        rgsrUsr: { type: "text", label: "L_RGSR_USR" },
        itmCn: { type: "text", label: "L_ITM_CN" },
    },
};

export const SF_SURV_ITM_INFO: TFormSchema = {
    id: "form_SurvItmInfo",
    schema: {
        bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_CD", all: true, area: "comnCd", comnCd: "COM0024" },
        ptlTpCd: {
            required: true,
            type: "select",
            label: "L_PTL_TP_CD",
            area: "comnCd",
            comnCd: "COM0052",
            excludes: ["B"],
            editType: "both",
        },
        qustnrTtle: { type: "text", label: "L_QUSTNR_TTLE" },
        quesCn: { type: "text", label: "L_QUES_CN" },
        survNm: { type: "text", label: "L_ORG_NM" },
        rgsrUsr: { type: "text", label: "L_RGSR_USR" },
        itmCnKO: { type: "text", label: "L_ITM_CN_KO", required: true },
        itmCnEN: { type: "text", label: "L_ITM_CN_EN", required: true },
        itmCnTZ: { type: "text", label: "L_ITM_CN_TZ", required: true },
        qestnrId: { type: "text", label: "L_QUSTNR_ID" }, // 설문지 정보
        quesId: { type: "text", label: "L_QUES_ID" },
        itmOrd: { type: "text", label: "L_ITM_ORD" }, // 질문 순번
        quesItmId: { type: "text", label: "L_QUES_ITM_ID" },
    },
};

export const SG_SURV_ITM_LIST: TGridSchema = {
    id: "grid_SurvItmList",
    options: { pagination: "out", index: true },
    head: [
        { cells: [{ header: "L_BSOP_CLSF_NM", binding: "bsopClsfCdNm", width: "*" }] ,id: "bsopClsfCd" },
        { cells: [{ header: "L_PTL_TP_CD", binding: "ptlTpCdNm", width: "*" }] ,id: "ptlTpCd" },
        { cells: [{ header: "L_QUSTNR_TTLE", binding: "qustnrTtle", width: "*" }] },
        { cells: [{ header: "L_QUES_CN", binding: "quesCn", width: "*" }] },
        { cells: [{ header: "L_ITM_CN", binding: "itmCn", width: "*" }] },
        { cells: [{ header: "L_FRST_REGST_ID", binding: "frstRegstId", width: "*" }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "bsopClsfCdNm", align: "center" }] },
        { cells: [{ binding: "ptlTpCdNm", align: "center" }] },
        { cells: [{ binding: "qustnrTtle", align: "left" }] },
        { cells: [{ binding: "quesCn", align: "left" }] },
        { cells: [{ binding: "itmCn", align: "left" }] },
        { cells: [{ binding: "frstRegstId", align: "center" }] },
        { cells: [{ binding: "frstRgsrDtm", type: "datetime" }] },
    ],
};

export const SG_SURV_ITM_LANG_LIST : TGridSchema = {
id: "grid_SurvItmLangList",
head: [
    { cells: [{ binding: "langCd"}] },
    { cells: [{ binding: "itmCn"}] },
],
body: [
    { cells: [{ binding: "langCd"}] },
    { cells: [{ binding: "itmCn"}] },
],
};

